import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Paragraph, TextField } from 'common/components';
import { openInNewTab } from 'common/helpers';
import { Button, checkPasswordStrength, PasswordField, PasswordStrength } from 'components/common';
import { PhoneField } from 'components/common/form';
import { Divider } from 'components/styled/Divider';
import { Flex } from 'components/styled/Flex';
import { LoginSignupWrapper } from 'components/styled/Form.styled';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from 'consts/contactInfo';
import { useRequiredParams } from 'helper/navigation/useRequiredParams';
import { keysToLowercase } from 'helper/objects';
import { formatPhone, isAdriagate } from 'modules/_common';
import { ButtonSpacing, LoginLink } from 'modules/account/features/login/components/index.styled';
import { TPasswordStrength } from 'modules/account/features/singup/models';
import { useAuthState } from 'services/auth/AuthContext';

import { useMutationRegisterRenter } from '../hooks';

export const SignupRenterSchema = Yup.object().shape({
  firstName: Yup.string().required('Required').max(100, 'First name is too long'),
  lastName: Yup.string().required('Required').max(100, 'Last name is too long'),
  phone: Yup.string()
    .required('Required')
    .matches(/^\+?[0-9]{6,14}$/, 'Invalid phone number'),
  field_a: Yup.string().email('Invalid email').required('Required'),
  field_b: Yup.string().required('Required').max(50, 'Password is too long'),
  repeatPassword: Yup.string()
    .max(50, 'Password is too long')
    .required('Required')
    .oneOf([Yup.ref('field_b'), null], "Passwords don't match")
    .required('Required'),
});

export const SignUpRenterForm = () => {
  const { customerId } = useRequiredParams(['customerId']);
  const { t } = useTranslation();
  const { colors, fontFamily } = useTheme();
  const {
    state: { isRegistrationPending },
    registerUser,
  } = useAuthState();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [passwordStrength, setPasswordStrength] = useState<TPasswordStrength[]>([]);

  //Mutation hooks
  const { error, mutate, status } = useMutationRegisterRenter({ registerUser });

  if (isRegistrationPending) {
    return <Navigate to="/sign-up-success?type=renter" />;
  }

  const createAccountSubmitForm = async (formValues: any) => {
    const isValidPassword = passwordStrength.every((item: TPasswordStrength) => item.isValid);

    if (!isValidPassword || !executeRecaptcha) return;

    // Generate reCAPTCHA token
    const token = await executeRecaptcha('signUpRenter');

    const { field_a, field_b, ...restValues } = formValues;

    const payload = {
      ...restValues,
      phone: formatPhone(formValues.phone, ''),
      email: formValues.field_a,
      password: formValues.field_b,
      recaptchaToken: token,
    };

    mutate({ customerId: customerId, email: formValues.field_a, payload });
  };

  return (
    <Formik
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialErrors={error ? keysToLowercase(error.response.data.Items) : null}
      enableReinitialize
      validationSchema={SignupRenterSchema}
      initialValues={{
        firstName: '',
        lastName: '',
        phone: '',
        field_a: '',
        field_b: '',
        repeatPassword: '',
      }}
      onSubmit={createAccountSubmitForm}
      validate={(values: any) => {
        const { field_b } = values;

        const passwordValidation = checkPasswordStrength(field_b);
        setPasswordStrength(passwordValidation);
      }}
    >
      {({ errors, touched, values, setFieldValue }) => (
        <LoginSignupWrapper autoComplete="off">
          <TextField label={t('First name')} name="firstName" isRequired />

          <TextField label={t('Last name')} name="lastName" isRequired />

          <PhoneField
            name="phone"
            label="Contact phone"
            setFieldValue={setFieldValue}
            value={values.phone}
            isValid={!errors['phone'] && touched['phone']}
            required
          />

          <TextField
            label={t('Email')}
            name="field_a"
            isRequired
            outsideValidationHasError={errors.field_a && touched.field_a}
          />

          <PasswordField
            mb="0.8rem"
            label="Password"
            name="field_b"
            isRequired
            autocomplete="new-password"
            error={errors.field_b && touched.field_b}
          />
          <PasswordStrength conditionValues={passwordStrength} />

          <PasswordField
            mt="1.25rem"
            label="Repeat new password"
            name="repeatPassword"
            isRequired
            autocomplete="new-password-new"
            error={errors.repeatPassword && touched.repeatPassword}
          />

          <ButtonSpacing marginTop="1.25rem">
            <Button
              isFetching={status === 'loading'}
              type="submit"
              label={isAdriagate ? 'Confirm' : 'Submit'}
              color={isAdriagate ? 'orange' : 'blue'}
              width="100%"
            />
          </ButtonSpacing>

          <Paragraph
            fontFamily={fontFamily.regular}
            fontSize="0.75rem"
            fontWeight="400"
            letterSpacing="0.013rem"
            margin="1.25rem 0 0 0"
            color={colors.black_80}
            display="block"
            textAlign="center"
          >
            {t('This site is protected by reCAPTCHA and the Google')}{' '}
            <LoginLink onClick={() => openInNewTab('https://policies.google.com/privacy')} to="#">
              {t('Privacy policy')}
            </LoginLink>{' '}
            {t('and')}{' '}
            <LoginLink onClick={() => openInNewTab('https://policies.google.com/terms')} to="#">
              {t('Terms of service_')}
            </LoginLink>{' '}
            {t('apply')}
          </Paragraph>

          <Paragraph
            fontFamily={fontFamily.regular}
            fontSize="0.75rem"
            fontWeight="400"
            letterSpacing="0.013rem"
            margin="1.25rem 0 0 0"
            justifyContent="center"
            color={colors.black_80}
          >
            {t('By creating an account, you agree to')}
          </Paragraph>

          <Flex justifyContent="center">
            <LoginLink onClick={() => openInNewTab(TERMS_OF_SERVICE_URL)} to="#">
              {t('Terms of service')}
            </LoginLink>
            <Paragraph
              fontFamily={fontFamily.regular}
              fontSize="0.75rem"
              fontWeight="400"
              letterSpacing="0.013rem"
              margin="0 0.125rem"
              color={colors.black_80}
            >
              {t('and')}
            </Paragraph>
            <LoginLink onClick={() => openInNewTab(PRIVACY_POLICY_URL)} to="#">
              {t('Privacy policy')}
            </LoginLink>
          </Flex>

          <Divider margin="1.25rem 0" />

          <Flex justifyContent="space-between">
            <Paragraph
              fontFamily={fontFamily.regular}
              fontSize="0.75rem"
              fontWeight="400"
              letterSpacing="0.013rem"
              margin="0"
              justifyContent="center"
            >
              {t('You already have an account?')}
            </Paragraph>

            <LoginLink to="/login" color={isAdriagate ? colors.ag_primary : colors.primary}>
              {t('Login here')}
            </LoginLink>
          </Flex>
        </LoginSignupWrapper>
      )}
    </Formik>
  );
};
